import {
    Component,
    ElementRef,
    forwardRef,
    HostListener,
    Input,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-range-slider',
    templateUrl: './range-slider.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RangeSliderComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class RangeSliderComponent implements ControlValueAccessor {
    @ViewChild('track') public trackRef: ElementRef<HTMLDivElement>;

    @Input() public min: number = 0;
    @Input() public max: number = 100;
    @Input() public step: number = 1;
    @Input() public disabled: boolean = false;
    @Input() public showValue: boolean = true;
    @Input() public showMinMax: boolean = false;

    public value: number = 0;
    public isDragging: boolean = false;

    @HostListener('document:mousemove', ['$event'])
    public onMouseMove(event: MouseEvent): void {
        if (!this.isDragging) return;
        this.updateValueFromPosition(event);
    }

    @HostListener('document:touchmove', ['$event'])
    public onTouchMove(event: TouchEvent): void {
        if (!this.isDragging) return;
        if (event.touches.length > 0) {
            this.updateValueFromTouch(event.touches[0]);
        }
    }

    @HostListener('document:mouseup')
    @HostListener('document:touchend')
    public onDragEnd(): void {
        this.isDragging = false;
    }

    public get percentage(): number {
        const range = this.max - this.min;
        if (range === 0) return 0;
        return ((this.value - this.min) / range) * 100;
    }

    public onTrackClick(event: MouseEvent): void {
        if (this.disabled) return;
        this.updateValueFromPosition(event);
    }

    public onThumbMouseDown(event: MouseEvent): void {
        if (this.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
        this.onTouched();
    }

    public onThumbTouchStart(event: TouchEvent): void {
        if (this.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
        this.onTouched();
    }

    public onKeyDown(event: KeyboardEvent): void {
        if (this.disabled) return;

        let newValue = this.value;

        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                newValue = this.roundToStepPrecision(
                    Math.min(this.value + this.step, this.max)
                );
                event.preventDefault();
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                newValue = this.roundToStepPrecision(
                    Math.max(this.value - this.step, this.min)
                );
                event.preventDefault();
                break;
            case 'Home':
                newValue = this.min;
                event.preventDefault();
                break;
            case 'End':
                newValue = this.max;
                event.preventDefault();
                break;
            default:
                return;
        }

        if (newValue !== this.value) {
            this.value = newValue;
            this.onChange(this.value);
        }
    }

    // ControlValueAccessor implementation
    public writeValue(value: number): void {
        this.value = value ?? this.min;
    }

    public registerOnChange(fn: (value: number) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private onChange: (value: number) => void = () => {
        // Default implementation
    };

    private onTouched: () => void = () => {
        // Default implementation
    };

    private updateValueFromPosition(event: MouseEvent): void {
        if (!this.trackRef) return;
        const rect = this.trackRef.nativeElement.getBoundingClientRect();
        this.calculateAndSetValue(event.clientX, rect);
    }

    private updateValueFromTouch(touch: Touch): void {
        if (!this.trackRef) return;
        const rect = this.trackRef.nativeElement.getBoundingClientRect();
        this.calculateAndSetValue(touch.clientX, rect);
    }

    private roundToStepPrecision(value: number): number {
        const stepStr = this.step.toString();
        const dotIndex = stepStr.indexOf('.');
        const decimals = dotIndex === -1 ? 0 : stepStr.length - dotIndex - 1;
        return parseFloat(value.toFixed(decimals));
    }

    private calculateAndSetValue(clientX: number, rect: DOMRect): void {
        // Calculate position considering RTL
        const isRtl = document.documentElement.dir === 'rtl';
        let position: number;

        if (isRtl) {
            position = (rect.right - clientX) / rect.width;
        } else {
            position = (clientX - rect.left) / rect.width;
        }

        // Clamp position between 0 and 1
        position = Math.max(0, Math.min(1, position));

        // Calculate value
        const range = this.max - this.min;
        let newValue = this.min + position * range;

        // Apply step and fix floating point precision
        newValue = Math.round(newValue / this.step) * this.step;
        newValue = this.roundToStepPrecision(newValue);

        // Clamp to min/max
        newValue = Math.max(this.min, Math.min(this.max, newValue));

        if (newValue !== this.value) {
            this.value = newValue;
            this.onChange(this.value);
        }
    }
}
